package er.prateek.crossly.ui.theme

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

enum class WidthClass { COMPACT, MEDIUM, EXPANDED }

enum class HeightClass { SHORT, REGULAR }

/**
 * The app's own breakpoints, measured from the actual composition constraints in
 * [AppTheme]. Deliberately not `material3-window-size-class`: this has to work
 * identically on a phone, a resized desktop window and a browser tab, and a
 * resizable desktop window changes class mid-session — so the source of truth is
 * the live constraint, not the physical device.
 */
@Immutable
class WindowSize(
    val width: Dp,
    val height: Dp,
) {
    val widthClass: WidthClass = when {
        width < 600.dp -> WidthClass.COMPACT
        width < 1000.dp -> WidthClass.MEDIUM
        else -> WidthClass.EXPANDED
    }

    val heightClass: HeightClass =
        if (height < 560.dp) HeightClass.SHORT else HeightClass.REGULAR

    val isCompact: Boolean get() = widthClass == WidthClass.COMPACT
    val isExpanded: Boolean get() = widthClass == WidthClass.EXPANDED
    val isShort: Boolean get() = heightClass == HeightClass.SHORT

    /**
     * True when there is room to put the board and its side panel next to each
     * other.
     *
     * Landscape proportion matters as much as raw width: a short, wide window
     * (a landscape phone, a half-height desktop window) has no vertical room for
     * a stacked board *and* keyboard, and is exactly the case the side-by-side
     * layout rescues. A portrait tablet is wide but tall, and stays stacked.
     */
    val isSideBySide: Boolean get() = width >= 640.dp && width > height

    /** Caps line length on desktop instead of stretching a phone layout to 2000dp. */
    val contentMaxWidth: Dp
        get() = when (widthClass) {
            WidthClass.COMPACT -> 560.dp
            WidthClass.MEDIUM -> 720.dp
            WidthClass.EXPANDED -> 1100.dp
        }

    val screenPadding: Dp
        get() = when (widthClass) {
            WidthClass.COMPACT -> 12.dp
            WidthClass.MEDIUM -> 20.dp
            WidthClass.EXPANDED -> 28.dp
        }

    /** Left/right screen margin -- slightly tighter than [screenPadding], which also sets the
     *  top/bottom margin, so the board and lists get a bit more usable width on a phone
     *  without reading as edge-to-edge on cards and buttons. */
    val screenPaddingHorizontal: Dp
        get() = when (widthClass) {
            WidthClass.COMPACT -> 16.dp
            WidthClass.MEDIUM -> 20.dp
            WidthClass.EXPANDED -> 28.dp
        }

    /** Picks the first value on compact, second on medium, third on expanded. */
    fun <T> pick(compact: T, medium: T = compact, expanded: T = medium): T =
        when (widthClass) {
            WidthClass.COMPACT -> compact
            WidthClass.MEDIUM -> medium
            WidthClass.EXPANDED -> expanded
        }
}

val LocalWindowSize = staticCompositionLocalOf {
    // Phone-sized default: any composable read outside AppTheme (previews, tests)
    // gets the tightest layout rather than an unbounded one.
    WindowSize(width = 400.dp, height = 800.dp)
}
